/**
 * API du widget RÉGULATEUR.
 *
 * Point d'entrée unique : `GET /devices/{uuid}/measures`. Le front ne connaît QUE l'uuid du
 * produit et n'a plus aucun lien avec la table Building (décision du 2026-07-30) — c'est DATA
 * qui résout, en SQL et en une fois, la chaîne régulateur → bâtiment → lot ouvert → mesures,
 * en y ajoutant les droits bigdata.
 *
 * Le bâtiment reste un maillon obligatoire côté serveur (`Batch` n'a d'autre ancrage que
 * `id_building`), mais il est désormais invisible d'ici. Partir du produit permet en plus de
 * VÉRIFIER qu'il s'agit bien d'un régulateur, ce que l'entrée par le bâtiment ne permettait pas.
 */
import { ENUM_DEVICE_TYPE } from "../generated/enums";
import { apiFetchRoute } from "./apiFetchRoute";

/**
 * Mesures journalières telles que BIGDATA les renvoie (une entrée par jour).
 *
 * Deux raisons pour qu'une valeur manque, à ne pas confondre :
 *  - le **bloc** est absent → droit d'accès non accordé (projection d'exclusion Mongo côté
 *    BIGDATA) ou grandeur jamais remontée par ce bâtiment ;
 *  - la **valeur est `null`** → le capteur existe mais n'a rien mesuré.
 *
 * Depuis le 2026-07-29 les champs numériques sont des `*float64` côté Go : un **`0` est une
 * vraie valeur mesurée** (température extérieure à 0 °C, conso nulle, zéro mort) et n'est plus
 * confondu avec « non mesuré », qui vaut désormais `null`. Ne jamais tester la véracité d'un
 * nombre (`if (value)`) — un 0 légitime serait ignoré ; toujours comparer à `null`/`undefined`.
 */
type MeasuredValue = number | null;

export type DailyMeasureEntry = {
  id?: string;
  data?: {
    /** Jour d'élevage. Absent pour un lot legacy dont la date d'entrée n'est pas connue. */
    day?: number;
    date?: string;
    animals?: Record<
      string,
      {
        total?: MeasuredValue;
        weight?: {
          average?: MeasuredValue;
          homogeneity?: MeasuredValue;
          gmq?: MeasuredValue;
          panel?: MeasuredValue;
        };
        dead?: {
          takenout?: MeasuredValue;
          eliminated?: MeasuredValue;
          natural?: MeasuredValue;
          cardiac?: MeasuredValue;
        };
      }
    >;
    consumption?: {
      // Listes (plusieurs compteurs par bâtiment), pas des scalaires.
      water?: number[] | null;
      food?: number[] | null;
      silo?: {
        name?: string;
        consumption?: MeasuredValue;
        stock?: MeasuredValue;
      }[];
    };
    ambience?: {
      airflow?: MeasuredValue;
      light?: MeasuredValue;
      co2?: MeasureRangeRaw;
      nh3?: MeasureRangeRaw;
      temperature?: {
        inside?: MeasureRangeRaw;
        outside?: MeasureRangeRaw;
      };
      humidity?: {
        inside?: MeasureRangeRaw;
        outside?: MeasureRangeRaw;
      };
    };
    energy?: { gaz?: MeasuredValue; electricty?: MeasuredValue };
  };
};

/**
 * Document `legacy_measure` (firmware Avibox) : 1 par box, jour et lot ; quatre tableaux d'envois
 * bruts empilés dans l'ordre de réception. Lu par `legacyToDailyMeasures`.
 */
type LegacyPost = { data?: Record<string, unknown>[] };
export type LegacyMeasureDoc = {
  /** Jour du document (ISO, minuit UTC). */
  date?: string;
  mesures?: LegacyPost[];
  pesees?: LegacyPost[];
  conso_general?: LegacyPost[];
  mortalites?: LegacyPost[];
};

/** Plage min/max brute : chaque borne peut être `null` (capteur muet sur cette borne). */
export type MeasureRangeRaw = { min?: MeasuredValue; max?: MeasuredValue };

/**
 * Réponse de `GET /devices/{uuid}/measures` (cf. handlers.GetRegulatorMeasures côté DATA).
 * Elle porte TOUT le contexte : trois états distincts donnent un tableau de mesures vide et ne
 * s'affichent pas pareil — pas de bâtiment, pas de lot ouvert, ou lot muet sur la période.
 */
export type RegulatorMeasuresResponse = {
  /** Absent quand le régulateur n'est installé dans aucun bâtiment (produit en stock). */
  building_id?: number;
  /** Vide quand le bâtiment n'a aucun lot ouvert (état normal entre deux bandes). */
  batch_id: string;
  batch_name?: string;
  /**
   * Date d'entrée du lot (`Batch.entrance_date`, envoyée par la box à l'ouverture du lot) : sert
   * au jour d'élevage des lots legacy. Absente sans lot ouvert (ou avec un DATA plus ancien) : pas de J.
   */
  entrance_date?: string;
  /** Bornes de la fenêtre interrogée (YYYY-MM-DD) : la réponse ne couvre que celle-ci. */
  from: string;
  to: string;
  /** Type du produit : serenity, avitouch, avibox, labelbox, vigebox. */
  device_type?: string;
  /** Droits bigdata sur le bâtiment de ce régulateur ; toujours présent, éventuellement vide. */
  granted: string[];
  /** Faux si aucun GroupAccess n'existe : pas de licence du tout sur ce bâtiment. */
  has_group_access: boolean;
  /** Forme des mesures : "serenity" (daily_measure) ou "legacy" (payloads firmware bruts). */
  source?: "serenity" | "legacy";
  measures: DailyMeasureEntry[];
  /** Documents bruts d'un lot Legacy (source === "legacy"). Forme totalement différente. */
  legacy_measures?: LegacyMeasureDoc[];
};

/** Types de produit acceptés par le widget : les régulateurs (miroir de `IsRegulatorType` côté DATA). */
const REGULATOR_TYPES: readonly string[] = ENUM_DEVICE_TYPE;

export const isRegulatorType = (type: string) => REGULATOR_TYPES.includes(type);

/** Cause d'un échec, pour afficher le bon message plutôt qu'un « aucun bâtiment » trompeur. */
export type RegulatorFetchError =
  | "unauthorized"
  | "forbidden"
  | "not-found"
  | "invalid"
  | "unavailable";

export class RegulatorFetchFailure extends Error {
  constructor(readonly reason: RegulatorFetchError) {
    super(reason);
  }
}

export const regulatorErrorFromStatus = (
  status: number
): RegulatorFetchError => {
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not-found";
    case 400:
      return "invalid";
    default:
      return "unavailable";
  }
};

const requestRegulatorMeasures = async (
  deviceUuid: string
): Promise<RegulatorMeasuresResponse> => {
  const res = await apiFetchRoute("getDevicesUuidMeasures", undefined, {
    params: { uuid: deviceUuid },
  });
  if (!res.ok) {
    throw new RegulatorFetchFailure(regulatorErrorFromStatus(res.status));
  }
  // DATA répond toujours un corps JSON sur 200 : vide ou illisible = panne, pas « rien à afficher ».
  try {
    const raw = await res.text();
    return JSON.parse(raw) as RegulatorMeasuresResponse;
  } catch {
    throw new RegulatorFetchFailure("unavailable");
  }
};

// Le configurateur et son aperçu demandent le même régulateur au même instant : un seul appel
// réseau pour les deux. Deux appels simultanés avec un jeton expiré renouvelaient la session en
// double, et le perdant déconnectait l'utilisateur.
const inFlight = new Map<string, Promise<RegulatorMeasuresResponse>>();

/**
 * Récupère les mesures récentes d'un régulateur, avec son contexte complet (un seul aller-retour,
 * DATA fait la résolution). Rejette avec une `RegulatorFetchFailure` qui porte la cause.
 */
export const fetchRegulatorMeasures = (
  deviceUuid: string
): Promise<RegulatorMeasuresResponse> => {
  const pending = inFlight.get(deviceUuid);
  if (pending) return pending;
  const request = requestRegulatorMeasures(deviceUuid).finally(() =>
    inFlight.delete(deviceUuid)
  );
  inFlight.set(deviceUuid, request);
  return request;
};
